import { apiData } from "../services/api_service";
import { productAvailabilityAPI } from "../config";

/**
 * Computes the reservation impact of changing a proforma's held quantities.
 *
 * Both cancel and edit reduce to: old reserved (this proforma's current hold)
 * vs new reserved (0 for cancel, the new form qty for edit) per product. The
 * batch availability endpoint returns `atp` (already net of this proforma's
 * current hold), so:
 *   availableBefore = atp
 *   availableAfter  = atp + oldReserved - newReserved
 *
 * @param {Object} p
 * @param {number|string} p.storeId
 * @param {Array} p.oldItems - current items ({productId|product_id, quantity, product_name?})
 * @param {Array} p.newItems - proposed items (empty => cancel)
 * @returns {Promise<Array>} impact rows
 */
export const computeReservationImpact = async ({
  storeId,
  oldItems = [],
  newItems = [],
}) => {
  const norm = (arr) => {
    const m = {};
    (arr || []).forEach((it) => {
      const pid = Number(it.productId ?? it.product_id);
      const qty = parseInt(it.quantity || 0, 10);
      if (!pid || qty <= 0) return;
      const name = it.product_name || it.name;
      if (!m[pid]) m[pid] = { qty: 0, name };
      m[pid].qty += qty;
      if (!m[pid].name && name) m[pid].name = name;
    });
    return m;
  };

  const oldMap = norm(oldItems);
  const newMap = norm(newItems);
  const productIds = [
    ...new Set([...Object.keys(oldMap), ...Object.keys(newMap)]),
  ].map(Number);

  if (!productIds.length || !storeId) return [];

  const atpMap = {};
  try {
    const res = await apiData("post", productAvailabilityAPI, {
      store_id: storeId,
      product_ids: productIds,
    });
    if (res.success && Array.isArray(res.data)) {
      res.data.forEach((a) => {
        atpMap[a.product_id] = a;
      });
    }
  } catch (e) {
    console.error("Reservation impact availability failed:", e);
  }

  return productIds.map((pid) => {
    const oldQ = oldMap[pid]?.qty || 0;
    const newQ = newMap[pid]?.qty || 0;
    const a = atpMap[pid] || {};
    const onHand = Number(a.onHand ?? 0);
    const atp = Number(a.atp ?? 0);
    return {
      product_id: pid,
      product_name: newMap[pid]?.name || oldMap[pid]?.name || `#${pid}`,
      store_name: a.store_name || "",
      onHand,
      oldReserved: oldQ,
      newReserved: newQ,
      delta: newQ - oldQ,
      availableBefore: atp,
      availableAfter: atp + oldQ - newQ,
    };
  });
};
